import { useEffect, useRef, useState, useCallback } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import { SHOP_LOCATION, DISTANCE_CHECK_URL } from '../config/siteConfig'

// Leaflet's default marker icon paths break under bundlers like Vite,
// which fingerprint asset filenames - this rewires them to the
// bundler-resolved URLs.
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow
})

const DEFAULT_ZOOM = 13
const DEBOUNCE_MS = 600

// Lets the customer drop/drag a pin marking their delivery location. Real
// road distance (not straight-line - straight-line badly underestimates
// real delivery distance in Karachi, where creeks and the coastline force
// long detours) is looked up from the Worker's /distance endpoint,
// debounced while dragging. Reports the full result back via
// onLocationChange whenever it changes; null while no pin is placed.
//
// On mount it also tries the Geolocation API and, if the customer allows
// it, drops the pin there automatically and looks up the distance right
// away - a manual "tap the map" step assumes the customer can read a map
// well enough to find their own street, which a lot of customers can't.
// A "Use My Location" button covers the case where the first prompt was
// dismissed or denied and they want to try again. Either way the pin stays
// fully draggable, since GPS/IP location is a starting point, not a
// guarantee of the exact address.
const DeliveryLocationPicker = ({ onLocationChange }) => {
  const mapContainerRef = useRef(null)
  const mapRef = useRef(null)
  const markerRef = useRef(null)
  const debounceRef = useRef(null)
  const requestIdRef = useRef(0)
  const placePinRef = useRef(null)
  const [pinned, setPinned] = useState(null)
  const [status, setStatus] = useState('idle') // idle | loading | ready | error
  const [result, setResult] = useState(null) // { distanceKm, zone, deliveryCharge, maxDeliveryKm }
  const [errorMessage, setErrorMessage] = useState(null)
  // Starts as 'locating' since the mount effect below tries geolocation
  // immediately - this way the UI already reads "Finding your location..."
  // on first paint instead of a placeholder that no browser callback would
  // ever update if geolocation is unsupported (see requestGeolocation).
  // permission-denied / unavailable / timeout map to the Geolocation API's
  // own PERMISSION_DENIED / POSITION_UNAVAILABLE / TIMEOUT error codes -
  // kept distinct (rather than one generic "denied") because "please turn
  // location back on" is the wrong instruction for a customer whose GPS
  // just hasn't gotten a fix yet, and telling everyone it was "blocked"
  // when the real cause is a slow signal is actively misleading.
  const [geoStatus, setGeoStatus] = useState('locating') // locating | done | permission-denied | unavailable | timeout | unsupported

  const fetchDistance = useCallback((lat, lng) => {
    setStatus('loading')
    setErrorMessage(null)
    const requestId = ++requestIdRef.current

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      try {
        const response = await fetch(DISTANCE_CHECK_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lat, lng })
        })
        const data = await response.json().catch(() => ({}))
        if (requestId !== requestIdRef.current) return // superseded by a newer pin move

        if (!response.ok) {
          setStatus('error')
          setErrorMessage(data.error || 'Could not calculate distance. Please try again.')
          return
        }
        setResult(data)
        setStatus('ready')
      } catch {
        if (requestId !== requestIdRef.current) return
        setStatus('error')
        setErrorMessage('Could not reach the server. Please check your connection and try again.')
      }
    }, DEBOUNCE_MS)
  }, [])

  // Runs when geolocation actually succeeds/fails - only ever invoked
  // asynchronously by the browser's own Geolocation API callbacks, never
  // synchronously from the mount effect below, so it's safe for these to
  // set state directly.
  const handleGeoSuccess = useCallback((pos) => {
    const { latitude, longitude } = pos.coords
    mapRef.current?.setView([latitude, longitude], DEFAULT_ZOOM)
    placePinRef.current?.(latitude, longitude)
    setGeoStatus('done')
  }, [])

  const handleGeoError = useCallback((error) => {
    // Keep the shop-centered default view and let the customer place the
    // pin themselves (or try the button again). error.code: 1 =
    // PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT.
    if (error?.code === 1) setGeoStatus('permission-denied')
    else if (error?.code === 3) setGeoStatus('timeout')
    else setGeoStatus('unavailable')
  }, [])

  // onUnsupported is only passed by the "Use My Location" button (a normal
  // click handler, not an effect) - the automatic mount-time attempt below
  // calls this with no argument, so on the rare browser without geolocation
  // it just silently stays in "locating" rather than setting state directly
  // from inside the effect.
  const requestGeolocation = useCallback((onUnsupported) => {
    if (!navigator.geolocation) {
      onUnsupported?.()
      return
    }
    navigator.geolocation.getCurrentPosition(handleGeoSuccess, handleGeoError, { timeout: 8000 })
  }, [handleGeoSuccess, handleGeoError])

  const locateMe = useCallback(() => {
    setGeoStatus('locating')
    requestGeolocation(() => setGeoStatus('unsupported'))
  }, [requestGeolocation])

  useEffect(() => {
    const map = L.map(mapContainerRef.current).setView(
      [SHOP_LOCATION.lat, SHOP_LOCATION.lng],
      DEFAULT_ZOOM
    )
    mapRef.current = map

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19
    }).addTo(map)

    const shopMarker = L.circleMarker([SHOP_LOCATION.lat, SHOP_LOCATION.lng], {
      radius: 7,
      color: '#166534',
      fillColor: '#22c55e',
      fillOpacity: 1
    }).addTo(map)
    shopMarker.bindTooltip('Our shop', { permanent: false })

    const placePin = (lat, lng) => {
      if (markerRef.current) {
        markerRef.current.setLatLng([lat, lng])
      } else {
        markerRef.current = L.marker([lat, lng], { draggable: true }).addTo(map)
        markerRef.current.on('dragend', () => {
          const pos = markerRef.current.getLatLng()
          setPinned({ lat: pos.lat, lng: pos.lng })
          fetchDistance(pos.lat, pos.lng)
        })
      }
      setPinned({ lat, lng })
      fetchDistance(lat, lng)
    }
    placePinRef.current = placePin

    map.on('click', (e) => placePin(e.latlng.lat, e.latlng.lng))

    return () => {
      map.remove()
      mapRef.current = null
      markerRef.current = null
      placePinRef.current = null
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [fetchDistance])

  // Tries geolocation once, automatically, right after the map is ready -
  // see the component doc comment for why this isn't left as a manual-only
  // step. Calls requestGeolocation (not locateMe) specifically so nothing
  // here calls setState synchronously within the effect body itself -
  // geoStatus already starts as 'locating', and every state update from
  // this point on only ever happens inside the Geolocation API's own async
  // callbacks.
  useEffect(() => {
    requestGeolocation()
  }, [requestGeolocation])

  useEffect(() => {
    if (!pinned) {
      onLocationChange?.(null)
      return
    }
    onLocationChange?.({
      lat: pinned.lat,
      lng: pinned.lng,
      status,
      distanceKm: result?.distanceKm ?? null,
      zone: result?.zone ?? null,
      deliveryCharge: result?.deliveryCharge ?? null,
      maxDeliveryKm: result?.maxDeliveryKm ?? null,
      errorMessage
    })
  }, [pinned, status, result, errorMessage, onLocationChange])

  const helperText = geoStatus === 'locating'
    ? 'Finding your location…'
    : geoStatus === 'done'
      ? "We placed a pin at your location - drag it if it's not quite right, or tap anywhere else on the map."
      : 'Tap the map to drop a pin at your delivery address, or drag the pin once placed.'

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-2">
        <p className="text-sm text-gray-600">{helperText}</p>
        {geoStatus !== 'locating' && (
          <button
            type="button"
            onClick={locateMe}
            className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold text-fresh-green hover:text-fresh-green-dark whitespace-nowrap"
          >
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="3" />
              <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
            </svg>
            Use My Location
          </button>
        )}
      </div>
      {geoStatus === 'permission-denied' && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2">
          Location access is off for this site. Please tap the map instead, or turn it on in your browser&apos;s site settings and try &quot;Use My Location&quot; again.
        </p>
      )}
      {geoStatus === 'unavailable' && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2">
          We couldn&apos;t determine your location right now. Please tap the map instead, or try &quot;Use My Location&quot; again.
        </p>
      )}
      {geoStatus === 'timeout' && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2">
          Finding your location took too long. Please try &quot;Use My Location&quot; again, or tap the map instead.
        </p>
      )}
      <div
        ref={mapContainerRef}
        className="w-full rounded-xl border border-gray-300 overflow-hidden"
        style={{ height: '320px' }}
      />
      {status === 'loading' && (
        <p className="text-sm text-gray-600 mt-2">Calculating road distance to your location…</p>
      )}
      {status === 'ready' && result?.distanceKm != null && (
        <p className="text-sm text-gray-600 mt-2">
          Pin dropped — <strong>{result.distanceKm.toFixed(1)} km</strong> by road from our shop.
        </p>
      )}
      {status === 'error' && (
        <p className="text-sm text-red-600 mt-2">{errorMessage}</p>
      )}
    </div>
  )
}

export default DeliveryLocationPicker
